import type { ViewTab } from "idfweb-workbench";

import type { NvsTab } from "./device-explorer.ts";
import type { NvsEntry } from "./nvs.ts";
import { copyText, formatValue, summary } from "./nvs-format.ts";
import "./nvs-view.css";

/**
 * A partition's entries as a table, grouped by namespace and sorted by key
 * in each, with a filter on namespace, key, type and the value's text.
 */
export function nvsView(tab: NvsTab): ViewTab {
    return {
        id: tab.id,
        title: tab.title,
        icon: "table",
        create: () => {
            const element = document.createElement("div");
            element.className = "idfweb-nvs";
            const heading = document.createElement("p");
            heading.className = "idfweb-nvs-summary";
            const filter = document.createElement("input");
            filter.type = "text";
            filter.placeholder = "Filter by namespace, key, type or value";
            filter.setAttribute("aria-label", filter.placeholder);
            filter.autofocus = true;
            const table = document.createElement("table");
            element.append(heading, filter, table);
            const draw = () => {
                const state = tab.state();
                filter.hidden = state?.state !== "read";
                if (state?.state !== "read") {
                    heading.textContent =
                        state === undefined
                            ? "Not read from this board. Open the partition from the Device section to read it."
                            : state.state === "reading"
                              ? "Reading…"
                              : state.reason;
                    table.replaceChildren();
                    return;
                }
                heading.textContent = summary(state.nvs);
                table.replaceChildren(...rows(tab, state.nvs.entries, filter.value));
            };
            filter.addEventListener("input", draw);
            draw();
            return { element, dispose: tab.watch(draw) };
        },
    };
}

function rows(tab: NvsTab, entries: NvsEntry[], query: string): HTMLElement[] {
    const words = query.toLowerCase().split(/\s+/).filter(Boolean);
    const groups = new Map<string, NvsEntry[]>();
    for (const entry of entries) {
        const cell = formatValue(entry);
        const text = `${entry.namespace} ${entry.key} ${entry.type} ${cell.text} ${cell.title}`.toLowerCase();
        if (!words.every((word) => text.includes(word))) continue;
        groups.set(entry.namespace, [...(groups.get(entry.namespace) ?? []), entry]);
    }
    const out: HTMLElement[] = [];
    for (const namespace of [...groups.keys()].sort()) {
        const head = document.createElement("tr");
        const th = document.createElement("th");
        th.colSpan = 3;
        th.scope = "colgroup";
        th.textContent = namespace;
        head.append(th);
        out.push(head);
        for (const entry of groups.get(namespace)!.sort((a, b) => a.key.localeCompare(b.key))) {
            const row = document.createElement("tr");
            const key = document.createElement("td");
            key.textContent = entry.key;
            const type = document.createElement("td");
            type.className = "idfweb-nvs-type";
            type.textContent = entry.type;
            row.append(key, type, valueCell(tab, entry));
            out.push(row);
        }
    }
    if (out.length === 0) {
        const row = document.createElement("tr");
        const cell = document.createElement("td");
        cell.colSpan = 3;
        cell.className = "idfweb-nvs-none";
        cell.textContent = entries.length === 0 ? "The partition holds no entries" : "No entries match";
        row.append(cell);
        out.push(row);
    }
    return out;
}

/** A value cut short opens whole in an editor tab when clicked; a string or a blob has a copy button. */
function valueCell(tab: NvsTab, entry: NvsEntry): HTMLElement {
    const cell = formatValue(entry);
    const td = document.createElement("td");
    const text = document.createElement(cell.cut ? "button" : "span");
    text.className = cell.cut ? "idfweb-nvs-value idfweb-nvs-open" : "idfweb-nvs-value";
    text.textContent = cell.text;
    text.title = cell.cut ? `${cell.title}\n\nClick to open the whole value in a tab.` : cell.title;
    if (text instanceof HTMLButtonElement) {
        text.type = "button";
        text.addEventListener("click", () => tab.open(entry));
    }
    td.append(text);
    const value = entry.value;
    if (typeof value === "string" || value instanceof Uint8Array) {
        if (cell.note !== "") {
            const note = document.createElement("span");
            note.className = "idfweb-nvs-note";
            note.textContent = cell.note;
            td.append(note);
        }
        const copy = document.createElement("button");
        copy.type = "button";
        copy.className = "idfweb-nvs-copy codicon codicon-copy";
        copy.title = typeof value === "string" ? "Copy the string" : "Copy the blob in hex";
        copy.setAttribute("aria-label", `${copy.title}: ${entry.namespace}/${entry.key}`);
        copy.addEventListener("click", () => void navigator.clipboard.writeText(copyText(value)));
        td.append(copy);
    }
    return td;
}
